import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { MapComponent } from '@components/entidades/common/map/map.component';
import { CatalogoCentrosPropios } from 'src/app/interfaces/interface-bakend/shared/catalogo/catalogo.centros-propios.interface';
import { CatalogoService } from 'src/app/services/shared/catalogo.service';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { environment } from '@env/environment';

@Component({
  selector: 'app-centro-propio-modificacion',
  templateUrl: './centro-propio-modificacion.component.html',
  styleUrls: ['./centro-propio-modificacion.component.scss']
})
export class CentroPropioModificacionComponent implements OnInit, AfterViewInit {
  form: FormGroup;
  catalogo: CatalogoCentrosPropios;
  @ViewChild(MapComponent) mapComponent: MapComponent;
  flagLoaded = false;

  // Propiedades para autocompletado de calle
  calleControl = new FormControl('');
  calleSuggestions: any[] = [];
  private calleSearchTerms = new Subject<string>();

  constructor(
    private router: Router,
    private catalogoService: CatalogoService,
    public guiMsjService: GuiMsjService
  ) {
    // Configurar búsqueda con debounce para la calle
    this.calleSearchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => this.searchCalleSuggestions(term))
    ).subscribe(suggestions => {
      this.calleSuggestions = suggestions;
    });
  }

  ngOnInit(): void {
    this.getEstadoVariables();
    this.createForm();

    // Separar la dirección existente en calle y altura
    if (this.catalogo?.properties?.direccion) {
      const direccion = this.catalogo.properties.direccion;
      const match = direccion.match(/^(.*?)\s+(\d+)$/);
      if (match) {
        this.form.patchValue({
          calle: match[1],
          altura: match[2]
        });
        this.calleControl.setValue(match[1]);
      } else {
        this.form.patchValue({ calle: direccion, altura: '' });
        this.calleControl.setValue(direccion);
      }
    }
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.updateMap();
  }

  get centroNoValido() {
    return this.form.get('centro').invalid && this.form.get('centro').touched;
  }

  get calleNoValida() {
    return this.form.get('calle').invalid && this.form.get('calle').touched;
  }

  get alturaNoValida() {
    return this.form.get('altura').invalid && this.form.get('altura').touched;
  }

  async getConcepto() {
    this.catalogo = this.catalogoService.getConceptoSeleccionado();
  }

  async getEstadoVariables() {
    if (this.catalogoService.getConceptoSeleccionado() === undefined) {
      this.router.navigateByUrl('/centros-propios-listado');
      return;
    } else {
      await this.getConcepto();
      this.flagLoaded = true;
    }
  }

  initMap() {
    const lat = this.form.get('latitud').value;
    const lng = this.form.get('longitud').value;
    if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
      this.mapComponent.setLocation(Number(lat), Number(lng));
    }
  }

  updateMap() {
    this.updatesCoordinates();
    const lat = this.form.get('latitud').value;
    const lng = this.form.get('longitud').value;
    if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
      this.mapComponent.setLocation(Number(lat), Number(lng));
    }
  }

  updatesCoordinates() {
    this.mapComponent.coordinatesChanged.subscribe((coordinates) => {
      console.log('Coordenadas actualizadas desde el mapa:', coordinates);
      this.form.patchValue({
        latitud: coordinates.latitud,
        longitud: coordinates.longitud
      });
    });
  }

  updateMarkerLocation() {
    const lat = this.form.get('latitud').value;
    const lng = this.form.get('longitud').value;
    if (this.mapComponent && lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
      this.mapComponent.setLocation(Number(lat), Number(lng));
    }
  }

  createForm() {
    // Convertir latitud y longitud a número para el formulario
    const latitud = this.catalogo?.properties?.latitud ? Number(this.catalogo.properties.latitud) : null;
    const longitud = this.catalogo?.properties?.longitud ? Number(this.catalogo.properties.longitud) : null;

    this.form = new FormGroup({
      centro: new FormControl(this.catalogo?.concepto, [
        Validators.required,
        Validators.minLength(3),
      ]),
      activa: new FormControl(this.catalogo?.logico_1),
      telefono: new FormControl(this.catalogo?.properties?.telefono),
      calle: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50)
      ]),
      altura: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(50)
      ]),
      latitud: new FormControl(latitud, [
        Validators.pattern(/^-?\d*(\.\d+)?$/),
      ]),
      longitud: new FormControl(longitud, [
        Validators.pattern(/^-?\d*(\.\d+)?$/),
      ])
    });
  }

  // --- Métodos para autocompletado de calle ---
  onCalleInput() {
    this.calleSearchTerms.next(this.calleControl.value);
  }

  private searchCalleSuggestions(query: string): Promise<any[]> {
    if (!query || query.length < 3) {
      return Promise.resolve([]);
    }
    const token = environment.mapBoxToken;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${token}&autocomplete=true&types=address&country=ar&limit=5`;
    return fetch(url)
      .then(response => response.json())
      .then(data => data.features || [])
      .catch(error => {
        console.error('Error fetching suggestions', error);
        return [];
      });
  }

  onCalleSelected(event: MatAutocompleteSelectedEvent) {
    const feature = event.option.value;
    if (!feature) return;
    const calle = feature.place_name.split(',')[0].trim();
    this.form.patchValue({ calle });
    this.calleControl.setValue(calle);
    if (this.form.get('altura')?.value) {
      this.buscarDireccionCompleta();
    }
  }

  displayCalleFn(suggestion: any): string {
    if (typeof suggestion === 'string') return suggestion;
    return suggestion?.place_name?.split(',')[0] || '';
  }

  // --- Búsqueda completa (calle + altura) ---
  buscarDireccionCompleta() {
    const calle = this.form.get('calle')?.value;
    const altura = this.form.get('altura')?.value;
    if (!calle || !altura) {
      alert('Debe ingresar calle y altura para buscar.');
      return;
    }
    const direccionCompleta = `${calle} ${altura}, Argentina`;
    if (this.mapComponent) {
      this.mapComponent.searchFromAddress(direccionCompleta)
        .then(() => {
          console.log('Mapa actualizado con la dirección:', direccionCompleta);
        })
        .catch(error => {
          console.error('Error al buscar dirección:', error);
          alert('No se pudo encontrar la dirección. Intente nuevamente.');
        });
    }
  }

  async setDataForma(): Promise<CatalogoCentrosPropios> {
    const direccionCompleta = `${this.form.value.calle} ${this.form.value.altura}`.trim();

    const data: CatalogoCentrosPropios = {
      key: null,
      concepto: this.form.value.centro,
      parent: this.catalogo.parent,
      logico_1: this.form.value.activa,
      properties: {
        direccion: direccionCompleta,
        telefono: this.form.value.telefono,
        latitud: this.form.value.latitud,
        longitud: this.form.value.longitud
      }
    };
    return data;
  }

  async put() {
    await this.guiMsjService.msjFormSubmit('Guardando');
    const data = await this.setDataForma();
    console.log('Datos a enviar al backend:', data);
    this.catalogoService.put(this.catalogo._id, data);
    await this.guiMsjService.msjFormSubmit('DatosGuardados');
    this.router.navigateByUrl('/centros-propios-listado');
  }
}